/**
 * g.format - date / number / money formatting for render callbacks.
 *
 *   g.format.date(pg)              '2026-07-01 00:00:00+03' -> '01.07.2026'
 *   g.format.datetime(pg)          -> '01.07.2026 14:30'
 *   g.format.time(pg)              '14:30:00+03' (or a timestamptz) -> '14:30'
 *   g.format.number(n, decimals?)  1234.5 -> '1,234.5' (Intl on g.lang)
 *   g.format.money(n, currency?)   1234.5 -> '€1,234.50' (g.settings.currency)
 *   g.format.relative(pg)          -> '3 days ago' / 'tomorrow'
 *
 * Date/time input is the WIRE format (Postgres timestamptz / date / timetz -
 * the same strings date-selekt holds); Date objects and epoch ms work too.
 * Output patterns come from g.settings.dateFormat / timeFormat (tokens: YYYY
 * MM DD HH hh mm A), names/separators from Intl on g.lang. The date/datetime/
 * time `style` argument switches to the locale's own look ('short' | 'medium'
 * | 'long' | 'full' -> Intl date/timeStyle) or overrides the token pattern.
 * Everything returns '' for empty/unparseable input - never throws (render
 * callbacks stay clean).
 */
import { settings } from './core.js';

const pad = (n) => String(n).padStart(2, '0');
const localeOf = () => String((window.g && window.g.lang) || settings.lang || 'en').replace('_', '-');
const INTL_STYLES = ['short', 'medium', 'long', 'full'];

const normalizeOffset = (off) => {
	const m = off.match(/^([+-])(\d{2}):?(\d{2})?$/);
	return m ? `${m[1]}${m[2]}:${m[3] || '00'}` : off;
};

// 'YYYY-MM-DD[ HH:mm[:ss]][±off]' -> local Date | null (same wire rules as date-selekt)
function parsePgTimestamp(str) {
	if (!str) return null;
	const m = String(str).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?)?$/);
	if (!m) return null;
	const [, y, mo, d, h = '00', mi = '00', s = '00', off] = m;
	if (!off) return new Date(+y, +mo - 1, +d, +h, +mi, +s);
	const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}${off === 'Z' ? 'Z' : normalizeOffset(off)}`);
	return isNaN(date) ? null : date;
}

// 'HH:mm[:ss][±off]' -> local Date (today) | null
function parsePgTime(str) {
	if (!str) return null;
	const m = String(str).trim().match(/^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/);
	if (!m) return null;
	const [, h, mi, , off] = m;
	const now = new Date();
	if (!off) return new Date(now.getFullYear(), now.getMonth(), now.getDate(), +h, +mi);
	const date = new Date(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${h}:${mi}:00${off === 'Z' ? 'Z' : normalizeOffset(off)}`);
	return isNaN(date) ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), +h, +mi) : date;
}

function toDate(value) {
	if (value == null || value === '') return null;
	if (value instanceof Date) return isNaN(value) ? null : value;
	if (typeof value === 'number') return new Date(value);
	return parsePgTimestamp(value);
}

function pattern(fmt, date) {
	const h24 = date.getHours();
	return fmt
		.replace(/YYYY/g, String(date.getFullYear()))
		.replace(/MM/g, pad(date.getMonth() + 1))
		.replace(/DD/g, pad(date.getDate()))
		.replace(/HH/g, pad(h24))
		.replace(/hh/g, pad(h24 % 12 || 12))
		.replace(/mm/g, pad(date.getMinutes()))
		.replace(/A/g, h24 < 12 ? 'AM' : 'PM');
}

function date(value, style) {
	const d = toDate(value);
	if (!d) return '';
	if (INTL_STYLES.includes(style)) return new Intl.DateTimeFormat(localeOf(), { dateStyle: style }).format(d);
	return pattern(style || settings.dateFormat || 'DD.MM.YYYY', d);
}

function datetime(value, style) {
	const d = toDate(value);
	if (!d) return '';
	if (INTL_STYLES.includes(style)) return new Intl.DateTimeFormat(localeOf(), { dateStyle: style, timeStyle: style === 'full' ? 'medium' : style }).format(d);
	return pattern(style || `${settings.dateFormat || 'DD.MM.YYYY'} ${settings.timeFormat || 'HH:mm'}`, d);
}

function time(value, style) {
	const d = (typeof value === 'string' && parsePgTime(value)) || toDate(value);
	if (!d) return '';
	if (INTL_STYLES.includes(style)) return new Intl.DateTimeFormat(localeOf(), { timeStyle: style === 'full' ? 'medium' : style }).format(d);
	return pattern(style || settings.timeFormat || 'HH:mm', d);
}

function number(value, decimals) {
	const n = typeof value === 'number' ? value : parseFloat(value);
	if (value == null || value === '' || isNaN(n)) return '';
	const opts = decimals != null ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : {};
	return new Intl.NumberFormat(localeOf(), opts).format(n);
}

function money(value, currency) {
	const n = typeof value === 'number' ? value : parseFloat(value);
	if (value == null || value === '' || isNaN(n)) return '';
	return new Intl.NumberFormat(localeOf(), { style: 'currency', currency: currency || settings.currency || 'EUR' }).format(n);
}

// "3 days ago" / "in 2 hours" / "yesterday" - Intl.RelativeTimeFormat picks
// the words, we pick the largest unit that fits.
const UNITS = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1]];

function relative(value) {
	const d = toDate(value);
	if (!d) return '';
	const diff = (d.getTime() - Date.now()) / 1000; // negative = past
	const abs = Math.abs(diff);
	const rtf = new Intl.RelativeTimeFormat(localeOf(), { numeric: 'auto' });
	for (const [unit, secs] of UNITS) {
		if (abs >= secs || unit === 'second') return rtf.format(Math.round(diff / secs), unit);
	}
	return '';
}

export const format = { date, datetime, time, number, money, relative };
